import { ChangeDetectorRef, Directive, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { parseNumber } from '@df/util/fn/parse-number';

export interface IFileInputEvent {
    file?: File;
    error?: Error;
    component: FileInputComponentDef;
}

@Directive()
export class FileInputComponentDef {
    @ViewChild('input')
    inputElementRef?: ElementRef<HTMLInputElement>;

    private _accept?: string;

    /**
     * Max file size
     */
    @Input()
    maxSize: string | number = '5MB';

    @Input()
    inputId?: string;

    /**
     * Max file size
     */
    @Input()
    variant?: string;

    /**
     * Accepted file formats
     */
    @Input()
    set accept(accept: string | undefined) {
        this._accept =
            accept &&
            accept
                .toLowerCase()
                .replace(/\s/g, '')
                .split(',')
                .filter(i => !!i)
                .map(extension => {
                    if (extension.indexOf('/') > -1 || extension[0] === '.') {
                        return extension;
                    }
                    return `.${extension}`;
                })
                .join(',');
    }

    get accept(): string | undefined {
        return this._accept;
    }

    /**
     * File change event listener
     */
    @Output()
    fileChange = new EventEmitter<IFileInputEvent>();

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    /**
     * Called on component initiation
     * Binds change event to child input (note that ng-change does not work with file input)
     */
    onChange(event: Event) {
        const input = <HTMLInputElement>event.target;
        const file = input.files?.[0];
        if (!file) {
            return;
        }
        if (this.maxSize) {
            const maxSize = typeof this.maxSize === 'string' ? parseNumber(this.maxSize) : this.maxSize;
            if (file.size > maxSize) {
                this.emit(new Error('Max file size is ' + this.maxSize));
                input.value = '';
                return;
            }
        }

        //Prevent from uploading invalid filetypes.

        const type = file.type?.toLowerCase();

        if (type && !this.isTypeAllowed(type)) {
            this.emit(new Error('Invalid file type'));
            this.reset();
            return;
        }

        this.emit(undefined, file);
        this.detectChanges();
    }

    private emit(error?: Error, file?: File): void {
        this.fileChange.emit({ error, file, component: this });
    }

    reset() {
        if (this.inputElementRef?.nativeElement) {
            this.inputElementRef.nativeElement.value = '';
        }
        this.detectChanges();
    }

    private isTypeAllowed(type: string): boolean {
        if (this.accept) {
            return !!this.accept.split(',').find(extension => {
                //if extension doesn't contain '/', remove dot from extension and check if bare form exists.
                if (extension.indexOf('/') === -1) {
                    return type.indexOf(extension.replace('.', '')) !== -1;
                } else {
                    if (extension.indexOf('/*') > -1) {
                        //if it contains "/*", look for general type (like "image/")
                        return type.indexOf(extension.slice(0, extension.indexOf('*'))) === 0;
                    } else {
                        //if it does contain "/" but no "/*", just do 1:1 compare
                        return extension === type;
                    }
                }
            });
        }
        return true;
    }

    private detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
